/**
 * Join — enter a room code and join.
 *
 * Flow:
 *  - If ?code= is present and valid: auto-join immediately (no form shown)
 *  - Otherwise: show room code input form
 *
 * Display name is always taken from the player store.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  normaliseRoomCode,
  isValidRoomCode,
} from '@playground/shared';
import { Button, Card, CardHeader } from '@playground/ui';
import { usePlayerStore } from '@/store/player.js';
import { useRoomStore } from '@/store/room.js';
import { Users, RefreshCw, ArrowRight, ArrowLeft } from 'lucide-react';
import { isEmbedMode } from '@/platform/embed.js';
import { isGlobalRoomCode } from '@/platform/games.js';
import type { Player } from '@playground/game-sdk';
import { apiFetch, apiFetchWithToken } from '@/platform/api.js';

/**
 * Whether a code is worth POSTing at the server.
 *
 * The syntactic check excludes `O`/`I` so *typed* codes can't be misread, but
 * several of the game-server's fixed global rooms use those letters anyway
 * (HORSER, PIXPKR, COINDL). Those rooms exist, and they are what a
 * self-contained game's invite link points at, so let them through too.
 */
function isJoinableCode(code: string): boolean {
  return isValidRoomCode(code) || isGlobalRoomCode(code);
}

interface JoinRoomResponse {
  token: string;
  gameId: string;
  players: Player[];
}

interface PublicRoom {
  roomId: string;
  gameId: string;
  gameName: string;
  playerCount: number;
  createdAt: number;
}

export function Join() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embed = isEmbedMode();

  const { playerId, displayName, setToken } = usePlayerStore();
  const { setRoom } = useRoomStore();

  const [code, setCode] = useState(searchParams.get('code') ?? '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteName, setSiteName] = useState('Playground');

  const autoJoinAttempted = useRef(false);

  const [publicRooms, setPublicRooms] = useState<PublicRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [joiningRoomId, setJoiningRoomId] = useState<string | null>(null);

  const fetchPublicRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      const res = await apiFetch('/api/rooms/public');
      if (res.ok) {
        const data = (await res.json()) as { rooms: PublicRoom[] };
        setPublicRooms(data.rooms);
      }
    } catch {
      // silently fail
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  useEffect(() => {
    const token = usePlayerStore.getState().token;
    if (token) {
      apiFetchWithToken('/api/games', token)
        .then((r) => r.json() as Promise<{ siteName?: string }>)
        .then((d) => { if (d.siteName) setSiteName(d.siteName); })
        .catch(() => {});
    }
    void fetchPublicRooms();
  }, [fetchPublicRooms]);

  // Sync code field when ?code= param changes
  useEffect(() => {
    const paramCode = searchParams.get('code');
    if (paramCode) setCode(paramCode);
  }, [searchParams]);

  // Auto-join if ?code= is present and valid
  useEffect(() => {
    if (autoJoinAttempted.current) return;
    const paramCode = searchParams.get('code');
    if (!paramCode) return;
    const normCode = normaliseRoomCode(paramCode);
    if (!isJoinableCode(normCode)) return;
    autoJoinAttempted.current = true;
    void performJoin(normCode, displayName || 'Anonymous');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function performJoin(normCode: string, name: string) {
    setLoading(true);
    setError('');
    // Read the current token from the store (may be null for first-time joiners)
    const currentToken = usePlayerStore.getState().token;
    try {
      const res = await apiFetch(`/api/rooms/${normCode}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
        },
        body: JSON.stringify({ playerId, displayName: name }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `Server error ${res.status}`);
      }

      const data = (await res.json()) as JoinRoomResponse;

      setToken(data.token);
      setRoom({ roomId: normCode, gameId: data.gameId, players: data.players, status: 'waiting' });
      // Self-contained games (the racing pair, pixel-poker) run their own
      // arenas/tables inside one fixed playground room, so their invite links
      // carry the arena code in `?join=`. The room URL has to keep it — the
      // game reads it off `window.location` once it mounts.
      const deepJoin = searchParams.get('join');
      void navigate(`/room/${normCode}${deepJoin ? `?join=${encodeURIComponent(deepJoin)}` : ''}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join room.');
    } finally {
      setLoading(false);
    }
  }

  async function handleJoinPublicRoom(roomId: string) {
    setJoiningRoomId(roomId);
    await performJoin(roomId, displayName || 'Anonymous');
    setJoiningRoomId(null);
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    const normCode = normaliseRoomCode(code);
    if (!isJoinableCode(normCode)) {
      setError('Enter a valid 6-character room code (e.g. K7BNQ2).');
      return;
    }
    await performJoin(normCode, displayName || 'Anonymous');
  }

  // If auto-joining (code in URL), show a loading screen
  if (searchParams.get('code') && isJoinableCode(normaliseRoomCode(searchParams.get('code') ?? ''))) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        {error ? (
          <div className="text-center px-4">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Link to="/" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">&larr; Back to home</Link>
          </div>
        ) : (
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
        )}
      </div>
    );
  }

  // Shared public rooms section (used in both embed and default layouts)
  const publicRoomsSection = (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className={`h-4 w-4 ${embed ? 'text-gray-500 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`} />
          <h2 className={`text-sm font-semibold ${embed ? 'text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>
            Public Rooms
          </h2>
        </div>
        <button
          type="button"
          onClick={() => void fetchPublicRooms()}
          disabled={loadingRooms}
          className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${embed ? 'text-gray-500 dark:text-gray-400 hover:text-cyan-400 hover:bg-gray-800' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loadingRooms ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {publicRooms.length === 0 ? (
        <p className={`text-center text-sm py-6 ${embed ? 'text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>
          {loadingRooms ? 'Loading...' : 'No public rooms available right now.'}
        </p>
      ) : (
        <div className="space-y-2">
          {publicRooms.map((room) => (
            <div
              key={room.roomId}
              className={`flex items-center justify-between rounded-xl px-4 py-3 ${embed ? 'bg-gray-800/60 border border-gray-700' : 'border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800'}`}
            >
              <div className="min-w-0">
                <p className={`text-sm font-semibold truncate ${embed ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                  {room.gameName}
                </p>
                <p className={`text-xs ${embed ? 'text-gray-500 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {room.roomId} &middot; {room.playerCount} {room.playerCount === 1 ? 'player' : 'players'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleJoinPublicRoom(room.roomId)}
                disabled={joiningRoomId === room.roomId}
                className={`ml-3 shrink-0 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold active:scale-95 transition-all disabled:opacity-50 ${embed ? 'bg-cyan-400 text-black hover:bg-cyan-300' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
              >
                {joiningRoomId === room.roomId ? (
                  <div className={`h-3.5 w-3.5 rounded-full border-2 animate-spin ${embed ? 'border-black/30 border-t-black' : 'border-white/30 border-t-white'}`} />
                ) : (
                  <>
                    Join
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Embed mode layout — dark theme, back button uses history
  // ---------------------------------------------------------------------------
  if (embed) {
    return (
      <div className="min-h-dvh flex flex-col" style={{ background: 'linear-gradient(to bottom, #060D1A, #0D1520)' }}>
        {/* Back button — navigate(-1) returns to Create screen */}
        <div className="px-5 pt-14 pb-2">
          <button
            type="button"
            onClick={() => void navigate(-1)}
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-white transition-colors active:scale-95"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>

        <div className="px-5 pt-4 pb-2">
          <h1 className="text-2xl font-black uppercase text-white tracking-wide">Join a Room</h1>
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">Enter a code or pick a public room below.</p>
        </div>

        <div className="px-5 mt-4">
          {/* Code entry */}
          <div className="rounded-2xl bg-gray-900 px-5 py-5">
            <form onSubmit={(e) => void handleJoin(e)} className="space-y-3">
              <input
                id="roomCode"
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(''); }}
                placeholder="K7BNQ2"
                maxLength={6}
                className="w-full rounded-xl bg-gray-800 border border-gray-700 px-4 py-3 text-white placeholder-gray-500 text-center text-lg font-mono uppercase tracking-widest focus:outline-none focus:border-cyan-400 transition-colors"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              {error && (
                <p className="rounded-xl bg-red-900/30 px-4 py-2 text-sm text-red-400 text-center" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.97] transition-transform"
              >
                {loading ? (
                  <div className="h-5 w-5 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                ) : 'Join Room'}
              </button>
            </form>
          </div>

          {publicRoomsSection}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Default (desktop) layout
  // ---------------------------------------------------------------------------
  return (
    <div>
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between pl-[max(1rem,var(--safe-l))] pr-[max(1rem,var(--safe-r))] pt-[calc(0.75rem+var(--safe-t))] pb-3 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-100 dark:border-gray-800">
        <Link to="/" className="flex items-center gap-2">
          <img src="/favicon.png" alt="" className="w-7 h-7 rounded-md" />
          <span className="text-base font-extrabold tracking-tight text-indigo-700 dark:text-indigo-400">{siteName}</span>
        </Link>
        <Link to="/" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">&larr; Back</Link>
      </header>

      <main className="mx-auto max-w-sm px-4 py-12">
        <Card>
          <CardHeader title="Join a Room" subtitle="Enter the room code shared by your host." />

          <form onSubmit={(e) => void handleJoin(e)} className="space-y-4">
            <div>
              <label htmlFor="roomCode" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Room code
              </label>
              <input
                id="roomCode"
                type="text"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(''); }}
                placeholder="K7BNQ2"
                maxLength={6}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500 text-center text-lg font-mono uppercase tracking-widest focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
              />
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" fullWidth loading={loading}>
              Join Room
            </Button>
          </form>
        </Card>

        {publicRoomsSection}
      </main>
    </div>
  );
}
